<script>
  import { Bar } from 'vue-chartjs'
  import verticalCursorPlugin from './plugins/vertical-cursor'

  import StellarSdk from 'stellar-sdk'
  import { mapState } from 'vuex'

  let stellarServer = new StellarSdk.Server('https://horizon.stellar.org')
  StellarSdk.Network.usePublicNetwork()

  let tooltipTime = function (time) {
    return new Date(time).toLocaleString({}, {
      hour12: false
    })
  }

  const PADDING = 0.1 // A vertical padding for better prices display

  export default {
    extends: Bar,
    props: {
      resolution: {
        type: Number,
        default: 60 * 60 * 1000
      },
      from: {
        type: Date,
        default () {
          return new Date(Date.now() - 24 * 60 * 60 * 1000)
        }
      },
      to: {
        type: Date,
        default () {
          return new Date(Date.now())
        }
      }
    },
    data () {
      return {
        records: [],
        updatePricesTimer: null
      }
    },
    computed: {
      ...mapState(['baseAsset', 'quoteAsset']),
      timestamps () {
        let timestamps = []
        let length = (this.paddedTo - this.paddedFrom) / this.resolution

        for (let i = 0; i < length; i++) {
          timestamps.push(new Date(this.paddedFrom + i * this.resolution))
        }

        return timestamps
      },
      paddedRecords () {
        return this.timestamps.map((t) => {
          return this.records.find((r) => r.timestamp === t.valueOf())
        })
      },
      paddedFrom () { return this.pad(this.from) },
      paddedTo () { return this.pad(this.to) },
      minPrice () {
        return this.records.map((r) => parseFloat(r.close)).reduce((min, price) => Math.min(min, price), 99999999)
      },
      maxPrice () {
        return this.records.map((r) => parseFloat(r.close)).reduce((max, price) => Math.max(max, price), 0)
      },
      deltaToBoundary () { return (this.maxPrice - this.minPrice) / (1 - PADDING * 2) },
      paddedMinPrice () { return this.minPrice + this.deltaToBoundary },
      paddedMaxPrice () { return this.maxPrice - this.deltaToBoundary }
    },
    watch: {
      baseAsset () { this.delayedUpdatePrices() },
      quoteAsset () { this.delayedUpdatePrices() },
      records () { this.doRenderChart() }
    },
    mounted () {
      this.addPlugin(verticalCursorPlugin)
      this.updatePrices()
    },
    methods: {
      pad (date) {
        let x = Math.floor(date.valueOf())
        while (x % this.resolution !== 0) { x -= 1 }
        return x
      },
      doRenderChart () {
        let previousPrice = null
        this.renderChart({
          labels: this.timestamps.map((t) => t.getTime()),
          datasets: [{
            label: 'Close price',
            data: this.paddedRecords.map((record) => {
              if (record) {
                previousPrice = parseFloat(record.close).toFixed(8)
                return previousPrice
              } else return previousPrice
            }),
            borderColor: 'indigo',
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 0,
            borderJoinStyle: 'round',
            borderCapStyle: 'round',
            fill: false,
            type: 'line',
            yAxisID: 'A'
          }, {
            label: 'Volume',
            data: this.paddedRecords.map((record) => {
              if (record) {
                return parseFloat(record.counter_volume).toFixed(8)
              } else return 0.0
            }),
            type: 'bar',
            yAxisID: 'B'
          }]
        }, {
          tooltips: {
            mode: 'index',
            intersect: false,
            displayColors: false,
            cornerRadius: 0,
            xPadding: 10,
            yPadding: 10,
            caretPadding: 5,
            multiKeyBackground: null,
            caretSize: 0,
            position: 'nearest',
            filter: function (tooltipItem) {
              return tooltipItem.datasetIndex === 0
            },
            callbacks: {
              title (tooltipItem, data) {
                if (tooltipItem[0]) {
                  return tooltipTime(data['labels'][tooltipItem[0]['index']])
                }
              },
              label (tooltipItem, data) {
                return ''
              },
              afterLabel (tooltipItem, data) {
                let record = this._chart.options.records[tooltipItem.index]

                let openPrice = 'N/A'
                let closePrice = 'N/A'
                let volume = '0'

                if (record) {
                  openPrice = `${parseFloat(record.open).toFixed(8)} ${this._chart.options.assetCode}`
                  closePrice = `${parseFloat(record.close).toFixed(8)} ${this._chart.options.assetCode}`
                  volume = parseFloat(record.counter_volume).toFixed(8)
                }

                return [
                  `Open: ${openPrice}`,
                  `Close: ${closePrice}`,
                  `Volume: ${volume} ${this._chart.options.assetCode}`
                ]
              }
            }
          },
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            display: false
          },
          verticalCursor: {
            borderColor: 'gray',
            borderWidth: 1,
            dataIndex: 0
          },
          assetCode: this.quoteAsset.code,
          records: this.paddedRecords,
          scales: {
            xAxes: [{
              display: false
            }],
            yAxes: [{
              id: 'A',
              display: false,
              ticks: {
                display: false,
                beginAtZero: false,
                min: this.paddedMinPrice,
                max: this.paddedMaxPrice
              }
            }, {
              id: 'B',
              display: false,
              ticks: {
                display: false
              }
            }]
          }
        })
      },
      delayedUpdatePrices () {
        if (this.updatePricesTimer) {
          clearTimeout(this.updatePricesTimer)
          this.updatePricesTimer = null
        }

        this.updatePricesTimer = setTimeout(this.updatePrices, 100)
      },
      updatePrices () {
        stellarServer.tradeAggregation(
          new StellarSdk.Asset(this.baseAsset.code, this.baseAsset.issuer),
          new StellarSdk.Asset(this.quoteAsset.code, this.quoteAsset.issuer),
          this.from.valueOf(),
          this.to.valueOf(),
          this.resolution
        ).limit(Math.min(200, this.timestamps.length)).call().then((data) => {
          this.records = data.records
        })
      }
    }
  }
</script>

<style lang="sass" scoped>

</style>
